import React, { useContext, useState, useRef, useEffect } from 'react';
import ReactToPrint, { useReactToPrint } from 'react-to-print';
import { useMediaQuery } from 'react-responsive';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { Typography, Button, useTheme } from '@zydalabs/zac-react';
import { PrintIcon, GiftIcon } from '@zydalabs/zac-icons-react';
import { useMutation } from '@apollo/client';

import * as translations from 'constants/translations';
import { ORDER_STATUS } from 'constants/order';
import { context as localeContext } from 'context/locale';
import { context as userContext } from 'context/user';
import { Text } from 'components/service';
import { SplitButton } from 'components/kit';
import { ReactComponent as GiftReceiptIcon } from 'assets/gift-receipt.svg';
import { UPDATE_PRINTING_PARAMS } from 'components/service/Page/schemas';
import { useSelectedStore } from 'hooks';
import { EXTERNAL_ORDER_IDENTIFIER_TYPE } from '../../constants';
import OrderInvoice from '../../../OrderInvoice';

const OrderDetailsPrint = ({ order, canBeSendToCourier, selected, isDeliveryBusiness, deliveryCourier, legalData }) => {
  const { colors } = useTheme();

  const { externalOrderIdentifierLink, externalOrderIdentifierType } = deliveryCourier || {};
  const { gift, status, orderItems, paymentStatus } = order || {};
  const { lang, translate } = useContext(localeContext);
  const mobileLargeMQ = useMediaQuery({ maxWidth: 425 });
  const mediumToLrgMQ = useMediaQuery({ minWidth: 426, maxWidth: 1024 });
  const lrgScreenMQ = useMediaQuery({ minWidth: 1025 });
  const { settings, setSettings } = useContext(userContext);
  const componentRef = useRef();
  const [printAsGift, setPrintAsGift] = useState(false);
  const storeId = useSelectedStore();

  const { printingType, printingLanguage } = settings || {};

  const [printSize, setPrintSize] = useState(printingType);
  const [printLang, setPrintLang] = useState(printingLanguage);

  const pageStyleMediaQuery = `
  @media print {
    @page {
      size: ${printSize === 'Thermal' ? `3.15in ${10 + orderItems.length}in` : 'A4'};
      margin: 4%;
    }
  }
  `;

  const [updatePrintingSettings] = useMutation(UPDATE_PRINTING_PARAMS, {
    onCompleted: ({ updatePrintingParams }) => {
      const { printingLanguage: newPrintingLanguage, printingType: newPrintingType } = updatePrintingParams;
      setSettings({ ...settings, printingType: newPrintingType, printingLanguage: newPrintingLanguage });
    },
  });

  const handleUpdatePrintingSettings = () =>
    updatePrintingSettings({
      variables: {
        restaurantId: storeId,
        printingType: printSize,
        printingLanguage: printLang,
      },
    });

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: () => pageStyleMediaQuery,
  });
  const handlePrintType = (isGift = true) => {
    setPrintAsGift(isGift);
    handlePrint();
  };
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (!isFirstRender.current) {
      setPrintAsGift(printAsGift);
      handlePrint();
    }
  }, [printAsGift]);
  useEffect(() => {
    isFirstRender.current = false;
  }, []);

  return (
    <div className={cx('flex', lang === 'ar' && 'flex-row-reverse', !lrgScreenMQ && 'flex-wrap')}>
      {paymentStatus !== ORDER_STATUS.WAITING_FOR_PAYMENT &&
        paymentStatus !== ORDER_STATUS.PAYMENT_FAILED &&
        paymentStatus !== ORDER_STATUS.REDIRECT_URL &&
        paymentStatus !== ORDER_STATUS.PAYMENT_EXPIRED && (
          <>
            <div
              className={cx(
                'flex pb-3 items-start xl:flex-1 flex-col',
                lang === 'ar' && 'flex-row-reverse',
                lrgScreenMQ ? 'flex-col w-1/2' && (lang === 'ar' ? 'ml-2' : 'mr-2') : 'flex-col w-full',
              )}
            >
              <div className="mb-5 pb-3 border-b-2 border-gray-100 w-full">
                <Typography variant="heading16">
                  <Text value={translations.PRINT} />
                </Typography>
              </div>
              <div
                className={cx('flex w-full flex-col', lang === 'ar' && 'flex-row-reverse', !lrgScreenMQ && 'flex-wrap')}
              >
                <div className={cx('flex w-full', !lrgScreenMQ && 'flex-wrap', lang === 'ar' && 'flex-row-reverse')}>
                  <div
                    className={cx('flex-1 flex flex-row h-10 xl:pr-4', mediumToLrgMQ && 'mb-4', lrgScreenMQ ? '' : '')}
                    style={{
                      minWidth: mediumToLrgMQ ? '10rem' : mobileLargeMQ ? '100%' : '0',
                    }}
                  >
                    <div className="w-full p-1 rounded bg-gray-300 flex cursor-pointer">
                      <span
                        role="presentation"
                        className={cx(
                          'flex items-center justify-center flex-1 rounded p-3 text-xs',
                          printLang === 'en' && 'bg-white shadow',
                        )}
                        onClick={() => setPrintLang('en')}
                      >
                        English
                      </span>
                      <span
                        role="presentation"
                        className={cx(
                          'flex items-center justify-center flex-1 rounded p-3 text-xs',
                          printLang === 'ar' && 'bg-white shadow',
                        )}
                        onClick={() => setPrintLang('ar')}
                      >
                        عربي
                      </span>
                    </div>
                  </div>
                  <div
                    className={cx('flex-1 flex flex-row h-10 px-0 mt-4 md:mt-0')}
                    style={{
                      minWidth: mediumToLrgMQ ? '10rem' : mobileLargeMQ ? '100%' : '0',
                    }}
                  >
                    <div className="w-full p-1 rounded bg-gray-300 flex cursor-pointer">
                      <span
                        role="presentation"
                        className={cx(
                          'flex items-center justify-center flex-1 rounded p-3 text-xs',
                          printSize === 'Thermal' && 'bg-white shadow',
                        )}
                        onClick={() => setPrintSize('Thermal')}
                      >
                        Thermal
                      </span>
                      <span
                        role="presentation"
                        className={cx(
                          'flex items-center justify-center flex-1 rounded p-3 text-xs',
                          printSize === 'A4' && 'bg-white shadow',
                        )}
                        onClick={() => setPrintSize('A4')}
                      >
                        A4
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col w-full">
                  <div className={cx('flex-1 flex flex-row-reverse h-10 mt-4')}>
                    {gift ? (
                      canBeSendToCourier || mobileLargeMQ || mediumToLrgMQ ? (
                        <>
                          <ReactToPrint
                            trigger={() => (
                              <Button
                                variant="tertiary"
                                rounded
                                startIcon={<GiftIcon width="20px" color={colors.blue[600]} />}
                                isFluid
                                text={translate(translations.PRINT_GIFT_RECEIPT)}
                              />
                            )}
                            onBeforeGetContent={() => {
                              setPrintAsGift(true);
                              handleUpdatePrintingSettings();
                            }}
                            content={() => componentRef.current}
                            pageStyle={pageStyleMediaQuery}
                          />
                        </>
                      ) : (
                        <SplitButton
                          actionBtnTestId="print-gift-receipt"
                          dropBtnTestId="gift-split-arrow-btn"
                          type="button"
                          textColor="text-primary-base"
                          kind="quartary"
                          size="base"
                          icon={<GiftReceiptIcon />}
                          full
                          actionBtnText={translations.PRINT_GIFT_RECEIPT}
                          actionBtnOnClick={() => {
                            handleUpdatePrintingSettings();
                            handlePrintType();
                          }}
                          items={[
                            {
                              testId: 'print-receipt-only',
                              title: <Text value={translations.PRINT_RECEIPT} />,
                              onClick: () => {
                                handleUpdatePrintingSettings();
                                handlePrintType(false);
                              },
                            },
                          ]}
                        />
                      )
                    ) : (
                      <ReactToPrint
                        trigger={() => (
                          <Button
                            variant="tertiary"
                            startIcon={<PrintIcon width="20px" color={colors.blue[600]} />}
                            rounded
                            isFluid
                            text={translate(translations.PRINT_RECEIPT)}
                          />
                        )}
                        onBeforeGetContent={() => {
                          handleUpdatePrintingSettings();
                        }}
                        content={() => componentRef.current}
                        pageStyle={pageStyleMediaQuery}
                      />
                    )}
                    <div style={{ display: 'none' }}>
                      <OrderInvoice
                        invoiceRef={componentRef}
                        legalData={legalData}
                        order={order}
                        store={selected}
                        lang={printLang}
                        isGift={printAsGift}
                        printSize={printSize}
                      />
                    </div>
                  </div>
                  {!!gift && (canBeSendToCourier || mobileLargeMQ || mediumToLrgMQ) && (
                    <div className={cx('flex-1 flex flex-row-reverse h-10 mt-4', !canBeSendToCourier && 'xl:mt-0')}>
                      <ReactToPrint
                        trigger={() => (
                          <Button
                            variant="tertiary"
                            startIcon={<PrintIcon width="20px" color={colors.blue[600]} />}
                            rounded
                            isFluid
                            text={translate(translations.PRINT_RECEIPT)}
                          />
                        )}
                        onBeforeGetContent={() => setPrintAsGift(false)}
                        content={() => componentRef.current}
                        pageStyle={pageStyleMediaQuery}
                      />
                    </div>
                  )}
                </div>
              </div>
              {isDeliveryBusiness &&
                externalOrderIdentifierLink &&
                externalOrderIdentifierType?.toLowerCase() === EXTERNAL_ORDER_IDENTIFIER_TYPE.AWB && (
                  <div className="flex w-full px-2 mt-2 flex-col">
                    {status !== ORDER_STATUS.CANCELED && (
                      <Button
                        variant="tertiary"
                        rounded
                        isFluid
                        text={translate(translations.PRINT_AWB)}
                        onClick={() => window.open(externalOrderIdentifierLink, '_blank')}
                      />
                    )}
                  </div>
                )}
            </div>
          </>
        )}
    </div>
  );
};

OrderDetailsPrint.propTypes = {
  order: PropTypes.shape({
    gift: PropTypes.string,
    status: PropTypes.string,
    orderItems: PropTypes.shape({
      quantity: PropTypes.number,
      variant: PropTypes.shape({
        barCode: PropTypes.string,
        discountEnabled: PropTypes.bool.isRequired,
        discountedPrice: PropTypes.number,
        externalId: PropTypes.string.isRequired,
        id: PropTypes.string.isRequired,
        images: PropTypes.arrayOf(PropTypes.string).isRequired,
        prepTime: PropTypes.number.isRequired,
        price: PropTypes.number.isRequired,
        propertySections: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.object)),
        sku: PropTypes.string,
        titleAr: PropTypes.string.isRequired,
        titleEn: PropTypes.string.isRequired,
      }),
      paymentStatus: PropTypes.string,
    }),
  }),
  canBeSendToCourier: PropTypes.bool,
  selected: PropTypes.shape({
    timeZone: PropTypes.string,
    currency: PropTypes.shape({
      decimals: PropTypes.number.isRequired,
      titleAr: PropTypes.string.isRequired,
      titleEn: PropTypes.string.isRequired,
    }),
  }),
  isDeliveryBusiness: PropTypes.bool,
  deliveryCourier: PropTypes.shape({
    driverAssigned: PropTypes.bool.isRequired,
    driverName: PropTypes.string,
    driverPhoneNumber: PropTypes.number,
    referenceId: PropTypes.string,
    externalOrderIdentifierLink: PropTypes.string,
    externalOrderIdentifierType: PropTypes.string,
    trackingLink: PropTypes.string,
    hasDriverInfo: PropTypes.bool,
  }),
  legalData: PropTypes.shape({
    companyNameEn: PropTypes.string,
    companyNameAr: PropTypes.string,
    vatIdNumber: PropTypes.string,
  }),
};
export default OrderDetailsPrint;
